import { SOCKET_IO_PATH, type CharacterClientToServerEvents, type CharacterServerToClientEvents } from '@sorrem/shared';
import { useEffect } from 'react';
import { io, type Socket } from 'socket.io-client';
import { sorremApi, useAppDispatch } from '@sorrem-client/ui/store';
import { getStoredSessionToken, getStoredCanonicalAccountId } from '@sorrem-client/ui/shared/auth-session';
import { withCharacterUpdate, withoutCharacter } from '@sorrem-client/ui/shared/character-updates';
import { useSelectedCharacterId } from './use-selected-character-id';

/**
 * Keeps the selected character's cached data in step with the server.
 * Watches it over the `/characters` socket and swaps each pushed character
 * into the `getCharacters` cache (e.g. a crucible payout that lands after
 * Character Home loaded). The same socket says when the open-crucibles list
 * changes, so the Crucibles tab shows ones other players create. Mount once
 * per page; Character Home does.
 */
export function useCharacterUpdates(): void {
    const dispatch = useAppDispatch();
    const { selectedCharacterId } = useSelectedCharacterId();
    const accountId = getStoredCanonicalAccountId();

    useEffect(() => {
        const token = getStoredSessionToken();
        if (accountId === null || selectedCharacterId === null || token === null) {
            return undefined;
        }

        const socket: Socket<CharacterServerToClientEvents, CharacterClientToServerEvents> = io('/characters', {
            path: SOCKET_IO_PATH,
            auth: { token },
        });

        // Watching again on every (re)connect also resends the current
        // character, so nothing changed while disconnected is missed.
        socket.on('connect', () => {
            socket.emit('character:watch', { characterId: selectedCharacterId }, () => undefined);
        });

        socket.on('character:updated', (character) => {
            let didChange = false;
            dispatch(sorremApi.util.updateQueryData('getCharacters', accountId, (characters) => {
                const merged = withCharacterUpdate(characters, character);
                if (merged === null) {
                    return undefined;
                }
                didChange = true;
                return merged;
            }));

            // Skills, commissions, the journal and the Compendium come from their own
            // queries; a change that reached the character (a run's payout)
            // usually reached them too.
            if (didChange) {
                dispatch(sorremApi.util.invalidateTags([
                    { type: 'CharacterSkills', id: character.id },
                    { type: 'CharacterCommissions', id: character.id },
                    { type: 'Journal', id: character.id },
                    { type: 'Compendium', id: character.id },
                ]));
            }
        });

        socket.on('crucibles:changed', () => {
            dispatch(sorremApi.util.invalidateTags(['OpenCrucibles']));
        });

        socket.on('character:deleted', ({ characterId }) => {
            dispatch(sorremApi.util.updateQueryData('getCharacters', accountId, (characters) => (
                withoutCharacter(characters, characterId) ?? undefined
            )));
        });

        return (): void => {
            socket.disconnect();
        };
    }, [accountId, dispatch, selectedCharacterId]);
}
